import React, { useState, useEffect, useMemo } from "react";
import {
  Calendar,
  Clock,
  ArrowLeft,
  Heart,
  Share2,
  AlertCircle,
  CreditCard,
  X,
  User,
  Mail,
  Phone,
  Brain,
} from "lucide-react";
import { useParams, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet";
import toast from "react-hot-toast";
import { fetchPublicPsychologyConsultations, fetchPublicPsychologyProfileData, bookSession, verifySessionPayment, toggleLikeSessionApi } from "./humanPsychologyApi";
import { fetchUserDetails } from "../../userprofile/fetchUserDetails";

// Declare Razorpay for TypeScript
declare global {
  interface Window {
    Razorpay: any;
  }
}

interface Price {
  session?: number;
}

interface GeneratedSlot {
  id: string;
  weekday: string;
  date: string;
  startTime: string;
  endTime: string;
  capacity: string;
}

interface SessionDetail {
  _id: string;
  title: string;
  type: string;
  description: string;
  duration: string;
  durationUnit?: string;
  thumbnail?: string;
  prices?: Price;
  pricingType?: string;
  status: string;
  totalBookings?: number;
  generatedSlots?: GeneratedSlot[];
  doctorId?: string;
  rating?: number;
  reviews?: number;
  bookings?: any[];
  googleVideoLink?: string;
}

interface DoctorInfo {
  _id: string;
  firstName: string;
  lastName: string;
  bio?: string;
  profileIcon?: string;
  specializations?: string[];
  experience?: string;
}

const getUserIdFromToken = () => {
  const token = sessionStorage.getItem('mentorAccessToken') || sessionStorage.getItem('userAccessToken') || sessionStorage.getItem('adminAccessToken');
  if (!token) return null;
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(atob(base64).split('').map(function (c) {
      return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
    }).join(''));
    const decoded = JSON.parse(jsonPayload);
    return decoded.id || decoded.mentorId || null;
  } catch (e) {
    return null;
  }
};

export default function PsychologyBookSession() {
  const { doctorId, sessionId } = useParams<{
    doctorId?: string;
    sessionId?: string;
  }>();
  const navigate = useNavigate();

  const [session, setSession] = useState<SessionDetail | null>(null);
  const [doctorInfo, setDoctorInfo] = useState<DoctorInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isBookmarked, setIsBookmarked] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState<string | null>(null);
  const [showBookingForm, setShowBookingForm] = useState(false);
  const [showSlotAlert, setShowSlotAlert] = useState(false);

  const handleLikeSession = async () => {
    if (!sessionId) return;
    const userId = getUserIdFromToken();
    if (!userId) {
      toast.error("Please log in to like this session");
      navigate("/login");
      return;
    }

    const originalIsBookmarked = isBookmarked;
    setIsBookmarked(!isBookmarked);

    try {
      const res = await toggleLikeSessionApi(sessionId);
      if (!res.success) {
        setIsBookmarked(originalIsBookmarked);
        toast.error(res.message || "Failed to toggle like");
      } else {
        toast.success(res.isLiked ? "Added to favorites" : "Removed from favorites");
      }
    } catch (error) {
      setIsBookmarked(originalIsBookmarked);
      toast.error("An error occurred");
    }
  };

  // Booking Form State
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    age: "",
    phone: "",
    pricingOption: "session",
  });
  const [formErrors, setFormErrors] = useState<{ [key: string]: string }>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const fetchSessionData = async (
    currentDoctorId: string,
    currentSessionId: string
  ) => {
    try {
      setLoading(true);
      setError(null);

      // Fetch Doctor/Psychologist info
      const docResponse = await fetchPublicPsychologyProfileData(currentDoctorId);
      const docRes = docResponse?.data ?? docResponse;
      if (docRes?.success && docRes?.data) {
        setDoctorInfo({
          _id: docRes.data._id,
          firstName: docRes.data.firstName || "",
          lastName: docRes.data.lastName || "",
          bio: docRes.data.bio || "",
          profileIcon: docRes.data.profileIcon || "",
          specializations: docRes.data.specializations || docRes.data.skill || [],
          experience: docRes.data.experience || "",
        });
      }

      // Fetch Sessions list
      const response = await fetchPublicPsychologyConsultations(currentDoctorId);
      const res = response?.data ?? response;

      if (res && res.success && Array.isArray(res.data)) {
        const foundSession = res.data.find(
          (c: any) => c._id === currentSessionId
        );

        if (foundSession) {
          const normalizedSession: SessionDetail = {
            ...foundSession,
            duration: String(foundSession.duration || "60 mins"),
          };
          setSession(normalizedSession);
          if (normalizedSession.pricingType === "demo") {
            setFormData((prev) => ({ ...prev, pricingOption: "demo" }));
          }

          // Initialize isBookmarked if user has liked this session
          const userId = getUserIdFromToken();
          if (userId && Array.isArray(foundSession.likes)) {
            setIsBookmarked(foundSession.likes.includes(userId));
          }
        } else {
          setError("Session not found");
        }
      } else {
        setError(res?.message || "Failed to load sessions");
      }
    } catch (err) {
      console.error("❌ Error fetching session details:", err);
      setError("Failed to load session details");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!doctorId || !sessionId) {
      setSession(null);
      setError("Doctor ID or Session ID is missing");
      setLoading(false);
      return;
    }

    setShowBookingForm(false);
    setSelectedSlot(null);
    fetchSessionData(doctorId, sessionId);
  }, [doctorId, sessionId]);

  useEffect(() => {
    if (showBookingForm) {
      const loadUserData = async () => {
        const userId = getUserIdFromToken();
        if (userId) {
          try {
            const res = await fetchUserDetails();
            if (res && res.user) {
              const user = res.user;
              let calculatedAge = "";
              if (user.dob) {
                const dob = new Date(user.dob);
                const ageDiffMs = Date.now() - dob.getTime();
                const ageDate = new Date(ageDiffMs);
                calculatedAge = String(Math.abs(ageDate.getUTCFullYear() - 1970));
              }
              setFormData((prev) => ({
                ...prev,
                name: prev.name || `${user.firstname || ""} ${user.lastname || ""}`.trim(),
                email: prev.email || user.useremail || "",
                phone: prev.phone || user.phone || "",
                age: prev.age || calculatedAge,
              }));
            }
          } catch (error) {
            console.warn("Failed to fetch user details for prefill:", error);
          }
        }
      };
      loadUserData();
    }
  }, [showBookingForm]);

  const loadRazorpayScript = async (): Promise<boolean> => {
    if (window.Razorpay) {
      return true;
    }

    return new Promise((resolve) => {
      const existingScript = document.querySelector(
        'script[src="https://checkout.razorpay.com/v1/checkout.js"]'
      ) as HTMLScriptElement | null;

      if (existingScript) {
        if (existingScript.dataset.loaded === "true" || window.Razorpay) {
          resolve(true);
          return;
        }

        const handleLoad = () => {
          existingScript.dataset.loaded = "true";
          resolve(true);
        };

        const handleError = () => resolve(false);

        const fallbackCheck = window.setInterval(() => {
          if (window.Razorpay) {
            window.clearInterval(fallbackCheck);
            resolve(true);
          }
        }, 200);

        existingScript.addEventListener("load", handleLoad, { once: true });
        existingScript.addEventListener("error", handleError, { once: true });

        window.setTimeout(() => {
          window.clearInterval(fallbackCheck);
          resolve(!!window.Razorpay);
        }, 4000);
        return;
      }

      const script = document.createElement("script");
      script.src = "https://checkout.razorpay.com/v1/checkout.js";
      script.async = true;
      script.onload = () => {
        script.dataset.loaded = "true";
        resolve(true);
      };
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  // Get selected slot details
  const slot = useMemo(() => {
    if (!selectedSlot || !session?.generatedSlots) return null;
    return session.generatedSlots.find((s) => s.id === selectedSlot) || null;
  }, [selectedSlot, session]);

  const isSlotPast = (): boolean => {
    if (!slot?.date || !slot?.startTime) {
      return false;
    }

    const now = new Date();
    let slotDate: Date;

    if (slot.date.includes("/")) {
      const parts = slot.date.split("/");
      if (parts.length === 3) {
        const day = parseInt(parts[0], 10);
        const month = parseInt(parts[1], 10) - 1;
        let year = parseInt(parts[2], 10);
        if (year < 100) year += 2000;
        slotDate = new Date(year, month, day);
      } else {
        slotDate = new Date(slot.date);
      }
    } else {
      slotDate = new Date(slot.date);
    }

    if (Number.isNaN(slotDate.getTime())) {
      return false;
    }

    slotDate.setHours(0, 0, 0, 0);
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    today.setHours(0, 0, 0, 0);

    if (slotDate < today) {
      return true;
    }

    if (slotDate.getTime() === today.getTime()) {
      const currentTime = `${String(now.getHours()).padStart(2, "0")}:${String(
        now.getMinutes()
      ).padStart(2, "0")}`;
      const convertTimeFormat = (timeStr: string): string => {
        const parts = timeStr.toUpperCase().split(/[\s:]+/);
        if (parts.length < 2) return timeStr;
        let hours = parseInt(parts[0], 10);
        const minutes = parts[1];
        const ampm = parts[2];
        if (ampm) {
          if (ampm === "PM" && hours !== 12) hours += 12;
          else if (ampm === "AM" && hours === 12) hours = 0;
        }
        return `${String(hours).padStart(2, "0")}:${minutes}`;
      };
      const slotStartTime = convertTimeFormat(slot.startTime);
      if (slotStartTime < currentTime) {
        return true;
      }
    }

    return false;
  };

  const getAvailableSlots = () => {
    if (!session?.generatedSlots || session.generatedSlots.length === 0) {
      return [];
    }

    const now = new Date();
    return [...session.generatedSlots]
      .filter((s) => {
        const parseSlotDateTime = (sl: GeneratedSlot) => {
          const timeMatch = sl.startTime.match(/(\d{1,2}):(\d{2})\s*(AM|PM)/i);
          if (!timeMatch) return null;
          let slDate = new Date(sl.date);
          if (sl.date.includes("/")) {
            const parts = sl.date.split("/");
            if (parts.length === 3) {
              const day = parseInt(parts[0], 10);
              const month = parseInt(parts[1], 10) - 1;
              let year = parseInt(parts[2], 10);
              if (year < 100) year += 2000;
              slDate = new Date(year, month, day);
            }
          }
          if (isNaN(slDate.getTime())) return null;
          let hours = parseInt(timeMatch[1], 10);
          const minutes = parseInt(timeMatch[2], 10);
          const period = timeMatch[3].toUpperCase();
          if (period === "PM" && hours !== 12) hours += 12;
          if (period === "AM" && hours === 12) hours = 0;
          return new Date(
            slDate.getFullYear(),
            slDate.getMonth(),
            slDate.getDate(),
            hours,
            minutes
          );
        };
        const slotDateTime = parseSlotDateTime(s);
        return slotDateTime ? slotDateTime >= now : true;
      })
      .sort((a, b) => {
        const parseSlotTime = (sl: GeneratedSlot) => {
          let slDate = new Date(sl.date);
          if (sl.date.includes("/")) {
            const parts = sl.date.split("/");
            if (parts.length === 3) {
              const day = parseInt(parts[0], 10);
              const month = parseInt(parts[1], 10) - 1;
              let year = parseInt(parts[2], 10);
              if (year < 100) year += 2000;
              slDate = new Date(year, month, day);
            }
          }
          return slDate.getTime();
        };
        return parseSlotTime(a) - parseSlotTime(b);
      });
  };

  const availableSlots = useMemo(() => {
    return getAvailableSlots();
  }, [session]);

  const hasSlotsAvailable = availableSlots.length > 0;

  const handleBookingClick = () => {
    if (hasSlotsAvailable && !selectedSlot) {
      setShowSlotAlert(true);
      return;
    }
    setShowBookingForm(true);
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    if (formErrors[name]) {
      setFormErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const validateForm = () => {
    const errors: { [key: string]: string } = {};
    if (isSlotPast()) {
      toast.error("This slot has already passed.");
      return false;
    }

    if (!formData.name.trim()) errors.name = "Name is required";
    if (!formData.email.trim()) {
      errors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = "Invalid email address";
    }
    if (!formData.age.trim() || isNaN(Number(formData.age))) {
      errors.age = "Age must be a valid number";
    }
    if (!formData.phone.trim() || !/^\d{10}$/.test(formData.phone.replace(/\D/g, ""))) {
      errors.phone = "Invalid 10-digit phone number";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const getPrice = (): number => {
    if (formData.pricingOption === "demo" || session?.pricingType === "demo") {
      return 0;
    }
    return session?.prices?.session || 0;
  };

  const generateBookingId = (): string => {
    const d = new Date();
    return `CONS${d.getFullYear()}${String(d.getMonth() + 1).padStart(
      2,
      "0"
    )}${String(d.getDate()).padStart(2, "0")}${String(d.getHours()).padStart(
      2,
      "0"
    )}${String(d.getMinutes()).padStart(2, "0")}${Math.random()
      .toString(36)
      .substring(2, 5)
      .toUpperCase()}`;
  };

  const handleBookingSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    if (!selectedSlot) {
      toast.error("Please select a time slot first");
      return;
    }

    setIsSubmitting(true);

    try {
      const price = getPrice();
      const bookingId = generateBookingId();

      const queryParams = new URLSearchParams(window.location.search);
      const urlVendorId = queryParams.get("vendorId") || queryParams.get("mentorId");
      if (urlVendorId) {
          sessionStorage.setItem("referralVendorId", urlVendorId);
      }
      const vendorId = urlVendorId || sessionStorage.getItem("referralVendorId") || undefined;

      const bookingPayload = {
        sessionId: session?._id,
        doctorId,
        vendorId,
        slotId: selectedSlot,
        bookingId,
        name: formData.name,
        email: formData.email,
        age: formData.age,
        phone: formData.phone,
        pricingOption: formData.pricingOption,
        totalAmount: price,
        bookingDate: slot?.date,
        slotTime: `${slot?.startTime} - ${slot?.endTime}`,
        sessionTitle: session?.title,
        type: session?.type || "Psychology Session",
        googleVideoLink: session?.googleVideoLink || "",
      };

      // Free Booking
      if (price === 0) {
        const response: any = await bookSession(bookingPayload);
        if (response.success || response.bookingId) {
          toast.success("Session booked successfully!");
          setShowBookingForm(false);
          navigate("/payment-success", {
            state: {
              bookingId: response.bookingId || bookingId,
              amount: "0",
              serviceName: session?.title || "Psychology Session",
              date: slot?.date || new Date().toLocaleDateString('en-GB'),
              duration: slot ? `${slot.startTime} - ${slot.endTime}` : "Flexible",
              location: "Online (Video Call)"
            }
          });
        } else {
          toast.error(response.message || "Failed to book session");
        }
      } else {
        // Paid Booking
        const response: any = await bookSession(bookingPayload);
        if (response.data?.razorpayOrderId) {
          const razorpayLoaded = await loadRazorpayScript();
          if (!razorpayLoaded || !window.Razorpay) {
            toast.error("Payment gateway failed to load");
            setIsSubmitting(false);
            return;
          }

          const options = {
            key: process.env.REACT_APP_RAZORPAY_KEY_ID,
            order_id: response.data.razorpayOrderId,
            amount: response.data.amount,
            currency: response.data.currency || "INR",
            name: "Metafit Psychology",
            description: `${session?.title} - Booking`,
            handler: async (paymentResponse: any) => {
              setIsSubmitting(true);
              try {
                const verificationResponse: any = await verifySessionPayment({
                  razorpay_payment_id: paymentResponse.razorpay_payment_id,
                  razorpay_order_id: paymentResponse.razorpay_order_id,
                  razorpay_signature: paymentResponse.razorpay_signature,
                  pendingBookingData: response.data?.pendingBookingData,
                });

                if (verificationResponse.success) {
                  toast.success("Payment successful! Session booked.");
                  setShowBookingForm(false);
                  navigate("/payment-success", {
                    state: {
                      bookingId: verificationResponse.bookingId || bookingId,
                      amount: price.toString(),
                      serviceName: session?.title || "Psychology Session",
                      date: slot?.date || new Date().toLocaleDateString('en-GB'),
                      duration: slot ? `${slot.startTime} - ${slot.endTime}` : "Flexible",
                      location: "Online (Video Call)"
                    }
                  });
                } else {
                  toast.error(verificationResponse.message || "Payment verification failed");
                  setIsSubmitting(false);
                }
              } catch (error) {
                console.error("Payment verification error:", error);
                toast.error("Payment verification failed");
                setIsSubmitting(false);
              }
            },
            prefill: {
              name: formData.name,
              email: formData.email,
              contact: formData.phone,
            },
            theme: {
              color: "#7c3aed",
            },
            modal: {
              ondismiss: () => {
                toast.error("Payment cancelled");
                setIsSubmitting(false);
              }
            }
          };

          const razorpay = new window.Razorpay(options);
          razorpay.on("payment.failed", (paymentErrResponse: any) => {
            toast.error(`Payment failed: ${paymentErrResponse.error.description}`);
            setIsSubmitting(false);
          });
          razorpay.open();
        } else {
          toast.error(response.message || "Failed to create payment order");
          setIsSubmitting(false);
        }
      }
    } catch (error) {
      console.error("Error booking session:", error);
      toast.error("Failed to book session. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const getAbsoluteImageUrl = (imageUrl: string | undefined): string => {
    if (!imageUrl) return "";
    if (imageUrl.startsWith("http://") || imageUrl.startsWith("https://")) return imageUrl;
    const baseUrl = window.location.origin;
    return `${baseUrl}${imageUrl.startsWith("/") ? "" : "/"}${imageUrl}`;
  };

  const formatDateString = (dateString: string): string => {
    if (!dateString) return "TBA";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      weekday: "short",
      month: "short",
      day: "numeric",
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-violet-50 via-purple-50 to-indigo-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-violet-600"></div>
      </div>
    );
  }

  if (error || !session) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-violet-50 via-purple-50 to-indigo-50 flex items-center justify-center p-4">
        <div className="text-center bg-white p-8 rounded-3xl shadow-xl max-w-md w-full border border-purple-100">
          <Brain className="w-16 h-16 text-violet-400 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-800 mb-2">
            {error || "Session not found"}
          </h1>
          <p className="text-gray-500 mb-6">
            The session you are looking for might have been removed or is temporarily unavailable.
          </p>
          <button
            onClick={() => navigate(-1)}
            className="w-full bg-violet-600 text-white py-3 rounded-xl hover:bg-violet-700 transition-colors font-medium shadow-lg shadow-violet-200"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-violet-50 via-purple-50 to-indigo-50">
      <Helmet>
        <title>{session.title} - Psychology Session | MetaFit</title>
        <meta name="description" content={session.description} />
      </Helmet>

      {/* Header */}
      <div className="sticky top-0 z-50 bg-white/80 backdrop-blur-md border-b border-purple-100/50">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-violet-600 hover:text-violet-700 font-medium transition"
          >
            <ArrowLeft className="w-5 h-5" />
            Back to Profile
          </button>
          <div className="flex gap-2">
            <button
              onClick={handleLikeSession}
              className={`p-2 rounded-full transition-all ${isBookmarked
                ? "bg-rose-100 text-rose-600 animate-pulse"
                : "bg-gray-100 text-gray-600 hover:bg-rose-100 hover:text-rose-600"
                }`}
            >
              <Heart className="w-5 h-5" fill={isBookmarked ? "currentColor" : "none"} />
            </button>
            <button className="p-2 rounded-full bg-gray-100 text-gray-600 hover:bg-gray-200 transition">
              <Share2 className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Thumbnail */}
            {session.thumbnail && session.thumbnail.trim() && (
              <div className="rounded-3xl overflow-hidden shadow-lg border border-purple-100">
                <img
                  src={getAbsoluteImageUrl(session.thumbnail)}
                  alt={session.title}
                  className="w-full h-96 object-cover"
                  onError={(e) => {
                    e.currentTarget.onerror = null;
                    e.currentTarget.src = "https://via.placeholder.com/800?text=Psychology+Session";
                  }}
                />
              </div>
            )}

            {/* Title & Type */}
            <div className="bg-white p-6 rounded-3xl border border-purple-100 shadow-sm space-y-4">
              <div>
                <span className="inline-block bg-gradient-to-r from-violet-100 to-purple-100 text-violet-700 px-4 py-1.5 rounded-full text-xs font-bold border border-violet-200 mb-3 uppercase tracking-wider">
                  {session.type}
                </span>
                <h1 className="text-3xl font-extrabold text-gray-900 leading-tight">
                  {session.title}
                </h1>
              </div>

              {doctorInfo && (
                <div className="flex items-center gap-4 pt-4 border-t border-purple-50">
                  <div className="h-12 w-12 rounded-full overflow-hidden bg-violet-100 border-2 border-violet-200 flex-shrink-0">
                    {doctorInfo.profileIcon ? (
                      <img
                        src={getAbsoluteImageUrl(doctorInfo.profileIcon)}
                        alt={doctorInfo.firstName}
                        className="h-full w-full object-cover"
                      />
                    ) : (
                      <div className="h-full w-full flex items-center justify-center text-violet-700 font-bold">
                        {doctorInfo.firstName.charAt(0)}
                      </div>
                    )}
                  </div>
                  <div>
                    <p className="text-[10px] font-semibold tracking-wider text-gray-400 uppercase">Consultant</p>
                    <p className="text-sm font-bold text-gray-800">
                      Dr. {doctorInfo.firstName} {doctorInfo.lastName}
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* Description */}
            <div className="bg-white rounded-3xl p-6 border border-purple-100 shadow-sm">
              <h2 className="text-xl font-bold text-gray-900 mb-4">About This Session</h2>
              <p className="text-gray-600 leading-relaxed whitespace-pre-wrap break-words">
                {session.description}
              </p>
            </div>

            {/* Available Slots */}
            <div id="available-slots" className="bg-white rounded-3xl p-6 border border-purple-100 shadow-sm">
              <div className="flex items-center justify-between gap-3 mb-6">
                <h2 className="text-xl font-bold text-gray-900">Select Available Slot</h2>
                <span className="rounded-full bg-violet-100 px-3 py-1 text-xs font-bold text-violet-700">
                  {availableSlots.length} available
                </span>
              </div>

              {hasSlotsAvailable ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-h-[30rem] overflow-y-auto pr-1">
                  {availableSlots.map((s) => {
                    const bookedCount = (session.bookings || []).filter(
                      (booking: any) =>
                        booking.selectedSlot?.slotId === s.id ||
                        booking.selectedSlot?.id === s.id ||
                        booking.slot?.slotId === s.id
                    ).length;

                    const slotCapacity = parseInt(s.capacity, 10) || 1;
                    const remainingSeats = Math.max(slotCapacity - bookedCount, 0);
                    const isFull = remainingSeats === 0;

                    return (
                      <button
                        key={s.id}
                        onClick={() => {
                          if (!isFull) {
                            setSelectedSlot(s.id);
                          }
                        }}
                        disabled={isFull}
                        className={`rounded-2xl border-2 p-4 text-left transition-all ${isFull
                          ? "cursor-not-allowed border-red-100 bg-red-50/50 opacity-70"
                          : selectedSlot === s.id
                            ? "border-violet-600 bg-violet-50/50 shadow-md shadow-violet-100/50"
                            : "border-gray-200 bg-white hover:border-violet-300 hover:shadow-sm"
                          }`}
                      >
                        <div className="mb-3 flex items-start justify-between gap-3">
                          <div>
                            <p className="text-[10px] font-bold uppercase tracking-wider text-violet-600">
                              {s.weekday || "Scheduled Day"}
                            </p>
                            <p className="text-base font-bold text-gray-900">
                              {s.date}
                            </p>
                          </div>
                          {isFull ? (
                            <span className="rounded-full bg-red-500 px-2 py-0.5 text-[10px] font-bold text-white uppercase">
                              Full
                            </span>
                          ) : selectedSlot === s.id ? (
                            <span className="rounded-full bg-violet-600 px-2.5 py-0.5 text-[10px] font-bold text-white uppercase">
                              Selected
                            </span>
                          ) : (
                            <span className="rounded-full bg-violet-100 px-2.5 py-0.5 text-[10px] font-bold text-violet-700 uppercase">
                              Open
                            </span>
                          )}
                        </div>

                        <div className="space-y-2 text-xs text-gray-600">
                          <div className="flex items-center gap-2">
                            <Clock className="h-4 w-4 text-violet-500" />
                            <span>
                              {s.startTime} - {s.endTime}
                            </span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Calendar className="h-4 w-4 text-violet-500" />
                            <span>
                              {remainingSeats} of {slotCapacity} slot{slotCapacity > 1 ? "s" : ""} left
                            </span>
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              ) : (
                <div className="rounded-2xl border border-dashed border-purple-200 bg-purple-50/30 p-8 text-center">
                  <p className="text-base font-bold text-gray-700">
                    No upcoming slots available right now
                  </p>
                  <p className="mt-1 text-xs text-gray-500">
                    Please check back later or consult the doctor for additional slots.
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Sidebar Booking Panel */}
          <div className="space-y-6">
            <div className="bg-white rounded-3xl p-6 border border-purple-100 shadow-sm sticky top-24 space-y-6">
              <div>
                <h3 className="text-xl font-bold text-gray-900">Ready to Book?</h3>
                <p className="text-gray-500 text-xs mt-1">
                  Secure your appointment slot in just a few simple steps.
                </p>
              </div>

              {/* Price Display */}
              <div className="bg-gradient-to-br from-violet-50 to-purple-50 rounded-2xl p-4 border border-violet-100 flex justify-between items-center">
                <span className="text-gray-700 font-medium text-sm">Amount:</span>
                <span className="text-2xl font-extrabold text-violet-700">
                  {session.pricingType === "demo" ? "FREE" : `₹${session.prices?.session || 0}`}
                </span>
              </div>

              <button
                type="button"
                onClick={handleBookingClick}
                className="w-full bg-gradient-to-r from-violet-600 to-purple-600 text-white py-3.5 rounded-xl hover:from-violet-700 hover:to-purple-700 transition font-bold shadow-lg shadow-violet-200 flex items-center justify-center gap-2 active:scale-[0.98]"
              >
                BOOK APPOINTMENT
              </button>

              <button
                type="button"
                onClick={() => (window.location.href = `tel:${doctorInfo?.experience || "+918829912389"}`)}
                className="w-full bg-gray-50 hover:bg-gray-100 text-gray-700 py-3 rounded-xl transition font-semibold text-sm border border-gray-100"
              >
                Contact Specialist
              </button>

              {/* Session Summary info */}
              <div className="pt-4 border-t border-purple-50 space-y-3 text-xs">
                <div className="flex justify-between">
                  <span className="text-gray-500">Session Type</span>
                  <span className="font-bold text-gray-800">{session.type}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Selected Slot</span>
                  <span className="font-bold text-violet-600">
                    {slot ? `${slot.date} (${slot.startTime})` : "None"}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Slot selection alert dialog */}
      {showSlotAlert && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[100] p-4">
          <div className="bg-white rounded-3xl max-w-sm w-full shadow-2xl p-6 border border-purple-100 text-center space-y-6">
            <div className="flex items-center justify-center w-14 h-14 mx-auto bg-amber-100 rounded-full text-amber-600">
              <AlertCircle className="w-8 h-8" />
            </div>
            <div className="space-y-2">
              <h2 className="text-xl font-bold text-gray-900">Select Time Slot</h2>
              <p className="text-gray-500 text-sm">
                Please select a time slot from the schedule card before booking.
              </p>
            </div>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setShowSlotAlert(false)}
                className="flex-1 py-3 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 transition font-semibold text-sm"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowSlotAlert(false);
                  document.getElementById("available-slots")?.scrollIntoView({ behavior: "smooth", block: "center" });
                }}
                className="flex-1 py-3 bg-violet-600 text-white rounded-xl hover:bg-violet-700 transition font-semibold text-sm shadow-md shadow-violet-200"
              >
                Select Slot
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Booking Form Modal */}
      {showBookingForm && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[100] p-4 overflow-y-auto">
          <div className="bg-white rounded-3xl max-w-md w-full shadow-2xl overflow-hidden border border-purple-100 my-8">
            <div className="bg-gradient-to-r from-violet-600 to-purple-600 text-white p-5 flex justify-between items-center">
              <h2 className="text-lg font-bold">Appointment Registration</h2>
              <button onClick={() => setShowBookingForm(false)} className="hover:bg-white/20 p-1.5 rounded-lg transition">
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleBookingSubmit} className="p-6 space-y-4">
              {/* Slot Summary */}
              {slot && (
                <div className="bg-violet-50/50 border border-violet-100 rounded-2xl p-4 text-xs space-y-2">
                  <div className="flex justify-between">
                    <span className="text-gray-500">Appointment Date:</span>
                    <span className="font-bold text-gray-800">{slot.weekday}, {slot.date}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Appointment Time:</span>
                    <span className="font-bold text-gray-800">{slot.startTime} - {slot.endTime}</span>
                  </div>
                </div>
              )}

              {/* Input Fields */}
              <div className="space-y-3">
                <div>
                  <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">
                    Full Name
                  </label>
                  <div className="relative">
                    <User className="absolute left-3 top-3.5 h-4.5 w-4.5 text-gray-400" />
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      className={`w-full pl-10 pr-4 py-3 rounded-xl border-2 text-sm transition-colors ${formErrors.name ? "border-red-500 focus:border-red-600" : "border-gray-200 focus:border-violet-500"
                        } focus:outline-none`}
                      placeholder="John Doe"
                    />
                  </div>
                  {formErrors.name && <p className="text-red-500 text-xs mt-1">{formErrors.name}</p>}
                </div>

                <div>
                  <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">
                    Email Address
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-3.5 h-4.5 w-4.5 text-gray-400" />
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      className={`w-full pl-10 pr-4 py-3 rounded-xl border-2 text-sm transition-colors ${formErrors.email ? "border-red-500 focus:border-red-600" : "border-gray-200 focus:border-violet-500"
                        } focus:outline-none`}
                      placeholder="johndoe@gmail.com"
                    />
                  </div>
                  {formErrors.email && <p className="text-red-500 text-xs mt-1">{formErrors.email}</p>}
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">
                      Age
                    </label>
                    <input
                      type="text"
                      name="age"
                      value={formData.age}
                      onChange={handleInputChange}
                      className={`w-full px-4 py-3 rounded-xl border-2 text-sm transition-colors ${formErrors.age ? "border-red-500 focus:border-red-600" : "border-gray-200 focus:border-violet-500"
                        } focus:outline-none`}
                      placeholder="25"
                    />
                    {formErrors.age && <p className="text-red-500 text-xs mt-1">{formErrors.age}</p>}
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">
                      Phone Number
                    </label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-3.5 h-4.5 w-4.5 text-gray-400" />
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        className={`w-full pl-10 pr-4 py-3 rounded-xl border-2 text-sm transition-colors ${formErrors.phone ? "border-red-500 focus:border-red-600" : "border-gray-200 focus:border-violet-500"
                          } focus:outline-none`}
                        placeholder="9876543210"
                      />
                    </div>
                    {formErrors.phone && <p className="text-red-500 text-xs mt-1">{formErrors.phone}</p>}
                  </div>
                </div>
              </div>

              {/* Secure payment processing by Razorpay */}
              {getPrice() > 0 && (
                <div className="bg-blue-50 border border-blue-100 rounded-xl p-3 flex gap-2">
                  <CreditCard className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <p className="text-[11px] text-blue-700 leading-relaxed">
                    Secure checkout powered by Razorpay. Your transaction details are fully encrypted.
                  </p>
                </div>
              )}

              {/* Submit Buttons */}
              <div className="flex gap-3 pt-4 border-t border-purple-50">
                <button
                  type="button"
                  onClick={() => setShowBookingForm(false)}
                  className="flex-1 py-3 bg-gray-50 hover:bg-gray-100 text-gray-700 rounded-xl transition font-semibold text-sm border border-gray-100"
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="flex-1 py-3 bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700 text-white rounded-xl transition font-semibold text-sm shadow-md shadow-violet-200 flex items-center justify-center"
                >
                  {isSubmitting ? (
                    <span className="flex items-center gap-2">
                      <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></span>
                      Processing...
                    </span>
                  ) : getPrice() === 0 ? (
                    "Confirm Booking"
                  ) : (
                    `Pay ₹${getPrice()}`
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
